import React, { useEffect, useState } from "react";
import {
  getTestPreparationServices,
  updateTestPreparationService,
} from "../../../services/user.services";
import { use } from "react";
import { useDispatch } from "react-redux";
import {
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

function TestPrepTable({
  services,
  setServices,
  saveTestPrepServices,
  deleteService,
  coreServiceId,
}) {
  const [editId, setEditId] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const dispatch = useDispatch();

  // Handle Input Change in Edit Mode
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData({ ...editableData, [name]: value });
  };

  // Save Edit
  const saveEdit = async () => {
    try {
      const updatedData = {
        id: editId,
        exam_name: editableData.exam_name,
        price_for_one: editableData.price_for_one,
        price_for_three: editableData.price_for_three,
        private_coaching_price: editableData.private_coaching_price,
        additional_notes: editableData.additional_notes,
        coreservices_id: coreServiceId,
      };
      const doesExist = services.find(
        (service) => service.id === updatedData.id
      );
      if (!doesExist) {
        await saveTestPrepServices({
          id: editId,
          exam_name: editableData.exam_name,
          price_for_one: editableData.price_for_one,
          price_for_three: editableData.price_for_three,
          private_coaching_price: editableData.private_coaching_price,
          additional_notes: editableData.additional_notes,
          coreservices_id: coreServiceId,
        });
        setServices((prev) =>
          prev.map((item) =>
            item.id === editId
              ? { ...item, ...editableData, isNew: false }
              : item
          )
        );
      } else {
        await updateTestPreparationService(editId, updatedData);
        setServices((prev) =>
          prev.map((item) =>
            item.id === editId ? { ...item, ...updatedData } : item
          )
        );
      }

      setEditId(null);
      setEditableData({});
    } catch (error) {
      console.error(
        "Error updating the service:",
        error.response?.data || error.message
      );
    }
  };

  // Toggle Edit Mode
  const toggleEdit = (service) => {
    setEditId(service.id);
    setEditableData({
      id: service.id,
      exam_name: service.exam_name || "",
      price_for_one: service.price_for_one || "",
      price_for_three: service.price_for_three || "",
      private_coaching_price: service.private_coaching_price || "",
      additional_notes: service.additional_notes || "",
    });
  };

  // Delete Row
  const deleteRow = async (id) => {
    try {
      await deleteService(id);
      setServices(services.filter((service) => service.id !== id));
    } catch (error) {
      console.error("Error deleting the service:", error);
    }
  };

  const toggleSelect = (id) => {
    setServices(
      services.map((service) =>
        service.id === id
          ? { ...service, selected: !service.selected }
          : service
      )
    );
  };

  const cancelEdit = (id) => {
    if (services.find((service) => service.id === id)?.isNew) {
      setServices((prev) => prev.filter((service) => service.id !== id));
    }
    setEditId(null);
    setEditableData({});
  };

  // Toggle expanded state for mobile cards
  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Render input field
  const renderInput = (name, value, placeholder, type = "text") => (
    <input
      type={type}
      name={name}
      value={value}
      onChange={handleEditChange}
      placeholder={placeholder}
      className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    />
  );

  // Mobile Card Component
  const MobileCard = ({ service }) => {
    const isEditing = editId === service.id || service.isNew;
    const isExpanded = expandedRows.has(service.id);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-3">
        {/* Card Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                className="cursor-pointer"
                checked={service.selected}
                onChange={() => toggleSelect(service.id)}
              />
              <div>
                <h3 className="font-medium text-gray-900">
                  {isEditing
                    ? renderInput(
                        "exam_name",
                        editableData.exam_name ?? service.exam_name,
                        "Exam Name"
                      )
                    : service.exam_name || "Untitled Exam"}
                </h3>
                <p className="text-sm text-gray-500">ID: {service.id}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Action Buttons */}
              {isEditing ? (
                <>
                  <button
                    onClick={saveEdit}
                    className="text-green-600 hover:text-green-800 p-2"
                    title="Save"
                  >
                    <FaSave />
                  </button>
                  <button
                    onClick={() => cancelEdit(service.id)}
                    className="text-gray-600 hover:text-gray-800 p-2"
                    title="Cancel"
                  >
                    <FaTimes />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => toggleEdit(service)}
                    className="text-blue-600 hover:text-blue-800 p-2"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => deleteRow(service.id)}
                    className="text-red-600 hover:text-red-800 p-2"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </>
              )}

              {/* Expand/Collapse Button */}
              <button
                onClick={() => toggleExpanded(service.id)}
                className="text-gray-400 hover:text-gray-600 p-2"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price for 1 (in Rs)
                </label>
                {isEditing ? (
                  renderInput(
                    "price_for_one",
                    editableData.price_for_one ?? service.price_for_one,
                    "Price for 1"
                  )
                ) : (
                  <p className="text-blue-600 font-medium">
                    {service.price_for_one || "N/A"}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price for 3 (in Rs)
                </label>
                {isEditing ? (
                  renderInput(
                    "price_for_three",
                    editableData.price_for_three ?? service.price_for_three,
                    "Price for 3"
                  )
                ) : (
                  <p className="text-blue-600 font-medium">
                    {service.price_for_three || "N/A"}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Private Coaching (in Rs)
                </label>
                {isEditing ? (
                  renderInput(
                    "private_coaching_price",
                    editableData.private_coaching_price ??
                      service.private_coaching_price,
                    "Private Coaching Price"
                  )
                ) : (
                  <p className="text-blue-600 font-medium">
                    {service.private_coaching_price || "N/A"}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                {isEditing ? (
                  <textarea
                    name="additional_notes"
                    value={
                      editableData.additional_notes ?? service.additional_notes
                    }
                    onChange={handleEditChange}
                    placeholder="Additional Notes"
                    rows="3"
                    className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">
                    {service.additional_notes || "No notes"}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Mobile View */}
      <div className="block lg:hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Test Preparation Services
            </h3>
            <p className="text-sm text-gray-500">{services.length} items</p>
          </div>

          {services.length > 0 ? (
            services.map((service) => (
              <MobileCard key={service.id} service={service} />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No test preparation services available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <div className="container mx-auto p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-4 text-center">
                    <input type="checkbox" className="cursor-pointer" />
                  </th>
                  <th className="py-3 px-4 text-left">SL</th>
                  <th className="py-3 px-4 text-left">Exam Name</th>
                  <th className="py-3 px-4 text-left">Price for 1 (in Rs)</th>
                  <th className="py-3 px-4 text-left">Price for 3 (in Rs)</th>
                  <th className="py-3 px-4 text-left">
                    Private Coaching (in Rs)
                  </th>
                  <th className="py-3 px-4 text-left">Additional Notes</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length > 0 ? (
                  services.map((service) => (
                    <tr
                      key={service.id}
                      className="border-b border-gray-300 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={service.selected}
                          onChange={() => toggleSelect(service.id)}
                        />
                      </td>
                      <td className="py-3 px-4 text-center">{service.id}</td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="exam_name"
                            value={editableData.exam_name ?? service.exam_name}
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Exam Name"
                          />
                        ) : (
                          service.exam_name
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="price_for_one"
                            value={
                              editableData.price_for_one ??
                              service.price_for_one
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Price for 1"
                          />
                        ) : (
                          service.price_for_one
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="price_for_three"
                            value={
                              editableData.price_for_three ??
                              service.price_for_three
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Price for 3"
                          />
                        ) : (
                          service.price_for_three
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="private_coaching_price"
                            value={
                              editableData.private_coaching_price ??
                              service.private_coaching_price
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Private Coaching Price"
                          />
                        ) : (
                          service.private_coaching_price
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <textarea
                            name="additional_notes"
                            value={
                              editableData.additional_notes ??
                              service.additional_notes
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Additional Notes"
                            rows="2"
                          />
                        ) : (
                          <div
                            className="max-w-xs truncate"
                            title={service.additional_notes}
                          >
                            {service.additional_notes}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          {editId === service.id || service.isNew ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Save"
                              >
                                <FaSave />
                              </button>
                              <button
                                onClick={() => cancelEdit(service.id)}
                                className="text-gray-600 hover:text-gray-800 p-1"
                                title="Cancel"
                              >
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => toggleEdit(service)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => deleteRow(service.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-gray-500">
                      No test preparation services available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestPrepTable;
